import type {
  LucideIcon,
} from 'lucide-react'
import {
  BarChart3,
  Building2,
  ClipboardList,
  ContactRound,
  Database,
  DatabaseBackup,
  FileText,
  Flame,
  Flower2,
  FolderKanban,
  Grid3X3,
  Layers3,
  LayoutDashboardIcon,
  LucideShoppingBag,
  MapPin,
  Megaphone,
  PackagePlus,
  PackageSearch,
  Palette,
  ReceiptText,
  Ruler,
  Settings,
  Shapes,
  SwatchBook,
  Tag,
  Truck,
  UsersRound,
  WandSparkles,
  Warehouse,
} from 'lucide-react'
import {
  ADMIN_ROLE_PERMISSIONS,
  EXTERNAL_USER_ROLE_PERMISSIONS,
  INTERNAL_SHARED_ROLE_PERMISSIONS,
} from '~/lib/permissions'

export const CUSTOM_FORMAT_CRM_REFERENCE = 'FORM_PERS'

export const routes = {
  home: '/',
  login: '/login',
  forgot: '/forgot',
  reset: '/reset',
  configurator: '/configurator',
  cart: '/cart/shopping_basket',
  address: '/cart/address',
  payment: '/cart/payment',
  dashboard: '/dashboard',
  orders: '/dashboard/orders',
  analytics: '/dashboard/analytics',
  adminOrders: '/dashboard/admin-orders',
  users: '/dashboard/users',
  predefinedNotes: '/dashboard/predefined-notes',
  labels: '/dashboard/labels',
  simplifiedDesignSettings: '/dashboard/simplified-design-settings',
  productLines: '/dashboard/product-lines',
  projectAcquisitionChannels: '/dashboard/project-acquisition-channels',
  projectTypes: '/dashboard/project-types',
  thirdPartyCommercialImportances: '/dashboard/third-party-commercial-importances',
  thirdPartyPrescriberTypes: '/dashboard/third-party-prescriber-types',
  transportCompanies: '/dashboard/transport-companies',
  transportCercos: '/dashboard/transport-cercos',
  customTileSettings: '/dashboard/custom-tile-settings',
  transportLocations: '/dashboard/transport-locations',
  transportLocationNew: '/dashboard/transport-locations/new',
  transportLocationDetail: (countryId: string) =>
    `/dashboard/transport-locations/${countryId}`,
  contacts: '/dashboard/contacts',
  contactNew: '/dashboard/contacts/new',
  contactDetail: (contactId: string) => `/dashboard/contacts/${contactId}`,
  projects: '/dashboard/projects',
  projectNew: '/dashboard/projects/new',
  projectDetail: (projectId: string) => `/dashboard/projects/${projectId}`,
  thirdParties: '/dashboard/third-parties',
  thirdPartySubtypes: '/dashboard/third-party-subtypes',
  thirdPartyNew: '/dashboard/third-parties/new',
  thirdPartyDetail: (thirdPartyId: string) =>
    `/dashboard/third-parties/${thirdPartyId}`,
  budgets: '/dashboard/budgets',
  budgetNew: '/dashboard/budgets/new',
  budgetDetail: (quoteId: string) => `/dashboard/budgets/${quoteId}`,
  budgetDetailFromProject: (quoteId: string) =>
    `/dashboard/budgets/${quoteId}?from=project`,
  activities: '/dashboard/activities',
  activityTypesAdmin: '/dashboard/activity-types',
  catalog: '/dashboard/catalog',
  catalogDesigns: '/dashboard/catalog/designs',
  catalogFormats: '/dashboard/catalog/formats',
  catalogCustomDesigns: '/dashboard/catalog/custom-designs',
  catalogCustomFormats: '/dashboard/catalog/custom-formats',
  catalogCustomTiles: '/dashboard/catalog/custom-tiles',
  catalogCustomProducts: '/dashboard/catalog/custom-products',
  catalogColors: '/dashboard/catalog/colors',
  catalogDesignNew: '/dashboard/catalog/designs/new',
  catalogFormatNew: '/dashboard/catalog/formats/new',
  catalogCommercialColorNew: '/dashboard/catalog/colors/commercial/new',
  catalogColorMixNew: '/dashboard/catalog/colors/mixes/new',
  catalogDesignDetail: (designId: string) =>
    `/dashboard/catalog/designs/${designId}`,
  catalogFormatDetail: (formatId: string) =>
    `/dashboard/catalog/formats/${formatId}`,
  catalogCommercialColorDetail: (colorId: string) =>
    `/dashboard/catalog/colors/commercial/${colorId}`,
  catalogColorMixDetail: (mixId: string) =>
    `/dashboard/catalog/colors/mixes/${mixId}`,
  catalogMacetero: '/dashboard/catalog/macetero',
  catalogMonestir: '/dashboard/catalog/monestir',
  catalogCelosia: '/dashboard/catalog/celosia',
  catalogBarroAlta: '/dashboard/catalog/barro-alta',
  catalogZellige: '/dashboard/catalog/zellige',
  catalogProductLineProducts: (lineId: string) =>
    `/dashboard/catalog/product-lines/${lineId}`,
  catalogMaceteroNew: '/dashboard/catalog/macetero/new',
  catalogCelosiaNew: '/dashboard/catalog/celosia/new',
  catalogBarroAltaNew: '/dashboard/catalog/barro-alta/new',
  catalogZelligeNew: '/dashboard/catalog/zellige/new',
  catalogMaceteroDetail: (planterId: string) =>
    `/dashboard/catalog/macetero/${planterId}`,
  catalogCelosiaDetail: (latticeId: string) =>
    `/dashboard/catalog/celosia/${latticeId}`,
  catalogBarroAltaDetail: (barroAltaId: string) =>
    `/dashboard/catalog/barro-alta/${barroAltaId}`,
  catalogZelligeDetail: (zelligeId: string) =>
    `/dashboard/catalog/zellige/${zelligeId}`,
  dataMigration: '/dashboard/data-migration',
  dataMigrationBulk: '/dashboard/data-migration/bulk',
  dataMigrationMigratedProducts: '/dashboard/data-migration/migrated-products',
  dataMigrationMigratedProductDetail: (productId: string | number) =>
    `/dashboard/data-migration/migrated-products/${productId}`,
  adminOrderDetail: (orderNumber: string) =>
    `/dashboard/admin-orders/${orderNumber}`,
  transportCompanyDetail: (carrierId: string) =>
    `/dashboard/transport-companies/${carrierId}`,
  profile: '/dashboard/profile',
  svgUpload: '/dashboard/svg-upload',
  register: '/register',
  verify: '/verify',
}

export interface NavItem {
  titleId: string
  url: string
  exactMatch?: boolean
  disabled?: boolean
  external?: boolean
  shortcut?: [string, string]
  icon?: LucideIcon
  label?: string
  sidebarTitleId?: string
  description?: string
  isActive?: boolean
  items?: NavItem[]
  requiredPermissions?: readonly string[]
}

export interface NavItemWithChildren extends NavItem {
  items: NavItemWithChildren[]
}

export const navItems: NavItem[] = [
  {
    titleId: 'orders',
    url: routes.orders,
    icon: LucideShoppingBag,
    shortcut: ['p', 'p'],
    isActive: false,
    items: [],
    requiredPermissions: EXTERNAL_USER_ROLE_PERMISSIONS,
  },
  {
    titleId: 'dashboard',
    url: routes.dashboard,
    exactMatch: true,
    icon: LayoutDashboardIcon,
    shortcut: ['d', 'b'],
    isActive: false,
    items: [],
    requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
  },
  {
    titleId: 'contacts',
    url: routes.contacts,
    icon: ContactRound,
    shortcut: ['c', 't'],
    isActive: false,
    items: [],
    requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
  },
  {
    titleId: 'thirdParties',
    url: routes.thirdParties,
    icon: Building2,
    shortcut: ['t', 'p'],
    isActive: false,
    items: [],
    requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
  },
  {
    titleId: 'projects',
    url: routes.projects,
    icon: FolderKanban,
    shortcut: ['p', 'r'],
    isActive: false,
    items: [],
    requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
  },
  {
    titleId: 'budgets',
    url: routes.budgets,
    icon: ReceiptText,
    shortcut: ['b', 'q'],
    isActive: false,
    items: [],
    requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
  },
  {
    titleId: 'activities',
    url: routes.activities,
    icon: ClipboardList,
    shortcut: ['a', 'c'],
    isActive: false,
    items: [],
    requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
  },
  {
    titleId: 'catalog',
    url: routes.catalog,
    icon: PackageSearch,
    shortcut: ['c', 'a'],
    isActive: false,
    requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
    items: [
      { titleId: 'catalogDesigns', url: routes.catalogDesigns, icon: Palette },
      { titleId: 'catalogCustomDesigns', url: routes.catalogCustomDesigns, icon: Palette },
      { titleId: 'catalogFormats', url: routes.catalogFormats, icon: Shapes },
      { titleId: 'catalogCustomTiles', url: routes.catalogCustomTiles, icon: Shapes },
      { titleId: 'catalogCustomFormats', url: routes.catalogCustomFormats, icon: Ruler },
      { titleId: 'catalogCustomProducts', url: routes.catalogCustomProducts, icon: PackagePlus },
      { titleId: 'catalogColors', url: routes.catalogColors, icon: SwatchBook },
      { titleId: 'catalogMacetero', url: routes.catalogMacetero, icon: Flower2 },
      { titleId: 'catalogMonestir', url: routes.catalogMonestir, icon: Warehouse },
      { titleId: 'catalogCelosia', url: routes.catalogCelosia, icon: Grid3X3 },
      { titleId: 'catalogBarroAlta', url: routes.catalogBarroAlta, icon: Flame },
      { titleId: 'catalogZellige', url: routes.catalogZellige, icon: Layers3 },
    ],
  },
  {
    titleId: 'transportMenu',
    url: routes.transportCompanies,
    icon: Truck,
    isActive: false,
    items: [
      { titleId: 'transportCompanies', url: routes.transportCompanies, icon: Building2 },
      { titleId: 'transportCercos', url: routes.transportCercos, icon: PackageSearch },
      { titleId: 'transportLocations', url: routes.transportLocations, icon: MapPin },
    ],
    requiredPermissions: ADMIN_ROLE_PERMISSIONS,
  },
  {
    titleId: 'administrationMenu',
    url: routes.thirdPartySubtypes,
    icon: Settings,
    isActive: false,
    items: [
      { titleId: 'users', url: routes.users, icon: UsersRound },
      { titleId: 'customTileSettings', url: routes.customTileSettings, icon: Shapes },
      { titleId: 'predefinedNotes', url: routes.predefinedNotes, icon: FileText },
      { titleId: 'simplifiedDesignSettings', url: routes.simplifiedDesignSettings, icon: Settings },
      {
        titleId: 'thirdPartiesAndContacts',
        url: routes.thirdPartySubtypes,
        icon: ContactRound,
        requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
        items: [
          {
            titleId: 'thirdPartySubtypes',
            sidebarTitleId: 'common.sideMenu.thirdPartySubtypes',
            url: routes.thirdPartySubtypes,
            icon: Tag,
            requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
          },
          {
            titleId: 'thirdPartyCommercialImportances',
            sidebarTitleId: 'common.sideMenu.thirdPartyCommercialImportances',
            url: routes.thirdPartyCommercialImportances,
            icon: Tag,
            requiredPermissions: ADMIN_ROLE_PERMISSIONS,
          },
          {
            titleId: 'thirdPartyPrescriberTypes',
            sidebarTitleId: 'common.sideMenu.thirdPartyPrescriberTypes',
            url: routes.thirdPartyPrescriberTypes,
            icon: Tag,
            requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
          },
        ],
      },
      {
        titleId: 'users',
        url: routes.users,
        icon: UsersRound,
        requiredPermissions: ADMIN_ROLE_PERMISSIONS,
      },
      {
        titleId: 'predefinedNotes',
        url: routes.predefinedNotes,
        icon: FileText,
        requiredPermissions: ADMIN_ROLE_PERMISSIONS,
      },
      {
        titleId: 'activityTypesAdmin',
        url: routes.activityTypesAdmin,
        icon: ClipboardList,
        requiredPermissions: ADMIN_ROLE_PERMISSIONS,
      },
      {
        titleId: 'labels',
        url: routes.labels,
        icon: Tag,
        requiredPermissions: ADMIN_ROLE_PERMISSIONS,
      },
      {
        titleId: 'productLines',
        url: routes.productLines,
        icon: Layers3,
        requiredPermissions: ADMIN_ROLE_PERMISSIONS,
      },
      {
        titleId: 'projectAcquisitionChannels',
        url: routes.projectAcquisitionChannels,
        icon: Megaphone,
        requiredPermissions: ADMIN_ROLE_PERMISSIONS,
      },
      {
        titleId: 'projectTypes',
        url: routes.projectTypes,
        icon: Shapes,
        requiredPermissions: ADMIN_ROLE_PERMISSIONS,
      },
    ],
    requiredPermissions: INTERNAL_SHARED_ROLE_PERMISSIONS,
  },
  {
    titleId: 'analytics',
    url: routes.analytics,
    icon: BarChart3,
    shortcut: ['a', 'n'],
    isActive: false,
    items: [],
    requiredPermissions: ADMIN_ROLE_PERMISSIONS,
  },
  {
    titleId: 'adminOrders',
    url: routes.adminOrders,
    icon: LucideShoppingBag,
    shortcut: ['o', 'a'],
    isActive: false,
    items: [],
    requiredPermissions: ADMIN_ROLE_PERMISSIONS,
  },
  {
    titleId: 'dataMigrationMenu',
    url: routes.dataMigration,
    icon: Database,
    shortcut: ['d', 'm'],
    isActive: false,
    items: [
      { titleId: 'dataMigrationWizard', url: routes.dataMigration, icon: WandSparkles },
      { titleId: 'dataMigrationBulkMenu', url: routes.dataMigrationBulk, icon: DatabaseBackup },
      {
        titleId: 'dataMigrationMigratedProductsMenu',
        url: routes.dataMigrationMigratedProducts,
        icon: PackageSearch,
      },
    ],
    requiredPermissions: ADMIN_ROLE_PERMISSIONS,
  },
]

export const AUTH_ROUTES = [
  routes.login,
  routes.register,
  routes.verify,
  routes.forgot,
  routes.reset,
]

// Routes that can be accessed by all users
export const PUBLIC_ROUTES = [
  routes.configurator,
  routes.cart,
  routes.address,
  routes.payment,
  '/checkout/success',
  '/checkout/failure',
  '/checkout/transfer-instructions',
  // Mini cart routes
  '/macetero',
  '/celosia',
  '/barro-alta-temperatura',
  '/zellige',
  '/shareable-order',
  '/muestras',
]
